import { z } from 'zod/v4';

/** Offset bounds enforced by the legacy body schema (`minimum: -5`, `maximum: 5`). */
export const MITIGATION_OFFSET_MIN = -5;
export const MITIGATION_OFFSET_MAX = 5;

/**
 * Body of `POST /risk-analysis/mitigations`, ported from
 * `2025-06/risk-analysis-service/routes/schemas/v3/request/mitigation.js` → `create.body`.
 *
 * `meta` is deliberately absent: the legacy schema omitted it too, and the legacy
 * AJV config stripped it silently rather than rejecting, persisting `meta = NULL`.
 * Adding it here would start honouring client-supplied `meta` — a behaviour change
 * pinned against by the integration test.
 */
export const CreateMitigationBodySchema = z.object({
  assessmentId: z.string().min(1).max(36),
  riskId: z.string().min(1).max(36),
  metricId: z.string().min(1).max(36),
  forCompanyId: z.string().min(1).max(36),
  offset: z.number().min(MITIGATION_OFFSET_MIN).max(MITIGATION_OFFSET_MAX),
  remark: z.string().min(1),
});

export type CreateMitigationBody = z.infer<typeof CreateMitigationBodySchema>;

/**
 * Response of `POST /risk-analysis/mitigations`, ported from the `mitigation`
 * definition in `routes/schemas/v3/responses.js:445`.
 *
 * The legacy definition has `additionalProperties: false` and declares none of
 * `assessmentId` / `riskId` / `templateId` / `ruleId`, so `fast-json-stringify`
 * stripped them from the SQL row on the way out. Keeping them out preserves that.
 */
export const MitigationResourceSchema = z.object({
  mitigationId: z.string().min(1).max(36),
  blueprintId: z.string().min(1).max(36).nullable(),
  forCompanyId: z.string().min(1).max(36).nullable(),
  metricId: z.string().min(1).max(36).nullable(),
  ruleDatapointId: z.string().min(1).max(36).nullable().optional(),
  ruleDatapointVersion: z.string().min(1).max(36).nullable().optional(),
  mitigationType: z.string().min(1).max(128).nullable(),
  value: z
    .number()
    .min(MITIGATION_OFFSET_MIN)
    .max(MITIGATION_OFFSET_MAX)
    .nullable(),
  valueType: z.string().min(1).max(128).nullable(),
  remark: z.string().min(1).nullable(),
  createdByUserId: z.string().min(1).max(36).nullable(),
  createdAt: z.string().nullable(),
  meta: z.looseObject({}).nullable().optional(),
});

export type MitigationResource = z.infer<typeof MitigationResourceSchema>;

const LIST_ASSESSMENT_MITIGATIONS_DEFAULT_PAGE_SIZE = 15;
const LIST_ASSESSMENT_MITIGATIONS_MAX_PAGE_SIZE = 500;

/** Path params of `GET /risk-analysis/mitigations/assessments/:id`. */
export const ListAssessmentMitigationsParamsSchema = z.object({
  id: z.string().min(1).max(36),
});

export type ListAssessmentMitigationsParams = z.infer<
  typeof ListAssessmentMitigationsParamsSchema
>;

/**
 * Querystring of `GET /risk-analysis/mitigations/assessments/:id`, ported from
 * `listByAssessment.querystring` in the legacy `request/mitigation.js`. The
 * default page stays the legacy 15; `count` starts at 1 (legacy `0` silently
 * became 15). There was never a `sort`.
 */
export const ListAssessmentMitigationsQuerySchema = z.object({
  page: z.coerce.number().int().min(0).default(0),
  count: z.coerce
    .number()
    .int()
    .min(1)
    .max(LIST_ASSESSMENT_MITIGATIONS_MAX_PAGE_SIZE)
    .default(LIST_ASSESSMENT_MITIGATIONS_DEFAULT_PAGE_SIZE),
  /** Stringified JSON — see `FILTER_COLUMNS` in `list-manual-by-assessment.ts`. */
  filter: z.string().max(512).optional(),
});

export type ListAssessmentMitigationsQuery = z.infer<
  typeof ListAssessmentMitigationsQuerySchema
>;

/**
 * One row of `GET /risk-analysis/mitigations/assessments/:id` — a manual
 * mitigation (a "remark") with its risk's title.
 *
 * The fields both frontends read. The legacy `manualMitigation` definition also
 * declared `templateId`/`ruleId`, which nothing reads, and stripped
 * `forCompanyId`/`mitigationType` via `additionalProperties: false`.
 */
export const AssessmentMitigationSchema = z.object({
  mitigationId: z.string(),
  assessmentId: z.string(),
  riskId: z.string(),
  riskTitle: z.string().nullable(),
  blueprintId: z.string(),
  metricId: z.string(),
  remark: z.string().nullable(),
  value: z.number().nullable(),
  valueType: z.string(),
  meta: z.looseObject({}).nullable(),
  createdAt: z.string().nullable(),
  createdByUserId: z.string().nullable(),
});

export type AssessmentMitigation = z.infer<typeof AssessmentMitigationSchema>;
